import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    EthnicityDashboardFilter,
    EthnicityDistributionDto,
    EthnicityYearTotalDto,
    EthnicityGrandTotalDto,
} from 'generated/openApi';
import ApiService from 'services/apiService';

interface EthnicityDataContextType {
    data: EthnicityDistributionDto[];
    loading: boolean;
    error: string | null;
    filter: EthnicityDashboardFilter;
    activeSchoolYear: number | null;
    totalsByYear: EthnicityYearTotalDto[];
    grandTotal: EthnicityGrandTotalDto | null;
    updateFilter: (filter: Partial<EthnicityDashboardFilter>) => void;
    refetch: () => void;
}

const EthnicityDataContext = createContext<EthnicityDataContextType | undefined>(undefined);

export const EthnicityDataProvider = ({ children }: { children: React.ReactNode }) => {
    const [data, setData] = useState<EthnicityDistributionDto[]>([]);
    const [totalsByYear, setTotalsByYear] = useState<EthnicityYearTotalDto[]>([]);
    const [grandTotal, setGrandTotal] = useState<EthnicityGrandTotalDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeSchoolYear, setActiveSchoolYear] = useState<number | null>(null);
    const [filter, setFilter] = useState<EthnicityDashboardFilter>({
        schoolYears: [],
    });
    const [initialized, setInitialized] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const responseData = await ApiService.ethnicityDashboard.getEthnicityDistribution(filter);

            if (responseData.activeSchoolYear && !activeSchoolYear) {
                setActiveSchoolYear(responseData.activeSchoolYear);

                if (!initialized && filter.schoolYears.length === 0) {
                    setFilter(prev => ({
                        ...prev,
                        schoolYears: [responseData.activeSchoolYear]
                    }));
                    setInitialized(true);
                    return;
                }
            }

            setData(responseData.data || []);
            setTotalsByYear(responseData.totalsByYear || []);
            setGrandTotal(responseData.grandTotal || null);
        } catch (err) {
            setError('Failed to load ethnicity data');
            console.error('Error fetching ethnicity data:', err);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (initialized) {
            fetchData();
        }
    }, [filter]);

    const updateFilter = (updates: Partial<EthnicityDashboardFilter>) => {
        setFilter(prev => ({ ...prev, ...updates }));
    };

    const refetch = () => {
        fetchData();
    };

    return (
        <EthnicityDataContext.Provider
            value={{
                data,
                loading,
                error,
                filter,
                activeSchoolYear,
                totalsByYear,
                grandTotal,
                updateFilter,
                refetch,
            }}
        >
            {children}
        </EthnicityDataContext.Provider>
    );
};

export const useEthnicityData = () => {
    const context = useContext(EthnicityDataContext);
    if (!context) {
        throw new Error('useEthnicityData must be used within EthnicityDataProvider');
    }
    return context;
};